import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, Select, message, Typography, Tag, Popconfirm, InputNumber, Row, Col, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;
const { Option } = Select;

const VehiclesPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();

    const fetchVehicles = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/master/vehicles');
            setVehicles(data);
        } catch (error) {
            message.error('Failed to fetch vehicles');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicles();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await client.delete(`/master/vehicles/${id}`);
            message.success('Vehicle deleted successfully');
            fetchVehicles();
        } catch (error) {
            message.error('Failed to delete vehicle');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const onFinish = async (values) => {
        try {
            if (editingId) {
                await client.put(`/master/vehicles/${editingId}`, values);
                message.success('Vehicle updated successfully');
            } else {
                await client.post('/master/vehicles', values);
                message.success('Vehicle created successfully');
            }
            setIsModalVisible(false);
            fetchVehicles();
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || 'Operation failed');
        }
    };

    const columns = [
        {
            title: 'Plate Number',
            dataIndex: 'plate_number',
            key: 'plate_number',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Vehicle Model',
            dataIndex: 'model',
            key: 'model',
            render: (text) => <Typography.Text style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Payload Capacity',
            dataIndex: 'capacity_kg',
            key: 'capacity_kg',
            render: (val) => <Typography.Text strong>{parseFloat(val || 0).toLocaleString()} kg</Typography.Text>
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'} style={{ borderRadius: 12 }}>
                    {isActive ? 'Active' : 'Maintenance'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined style={{ color: '#3b82f6' }} />}
                        onClick={() => handleEdit(record)}
                        className="action-btn"
                    />
                    <Popconfirm
                        title="Delete Vehicle"
                        description="Are you sure you want to delete this vehicle?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
                            danger
                            className="action-btn"
                        />
                    </Popconfirm>
                </Space>
            ),
        }
    ].filter(col => col.key !== 'actions' || isAdmin);

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const stats = {
        total: vehicles.length,
        active: (vehicles || []).filter(v => v.is_active).length,
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Fleet Management</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage company vehicles used for hauling and delivery.</Typography.Text>
                </div>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={handleAdd}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px', width: isMobile ? '100%' : 'auto' }}
                    >
                        Add Vehicle
                    </Button>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={12} md={8}>
                    <StatCard
                        title="Total Fleet"
                        value={stats.total}
                        icon={<i className="fa-solid fa-truck-moving"></i>}
                        color="#3b82f6"
                        subText="Total registered units"
                    />
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <StatCard
                        title="Available Units"
                        value={stats.active}
                        icon={<i className="fa-solid fa-circle-check"></i>}
                        color="#10b981"
                        subText="Ready for operations"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={vehicles}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 900 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title={editingId ? 'Edit Vehicle Info' : 'Register New Fleet Unit'}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={500}
                className="premium-modal"
            >
                <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ is_active: true }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="plate_number" label="Plate Number" rules={[{ required: true, message: 'Plate is required' }]}>
                                <Input placeholder="e.g. ABC 1234" style={{ height: 40, borderRadius: 8 }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="capacity_kg" label="Capacity (kg)">
                                <InputNumber min={0} style={{ width: '100%', height: 40, borderRadius: 8, paddingTop: 4 }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="model" label="Vehicle Model" rules={[{ required: true, message: 'Model is required' }]}>
                        <Input style={{ height: 40, borderRadius: 8 }} placeholder="e.g. Isuzu Elf" />
                    </Form.Item>

                    <Form.Item name="is_active" label="Operational Status">
                        <Select style={{ height: 40 }}>
                            <Option value={true}>Active (In Operation)</Option>
                            <Option value={false}>Inactive (Maintenance)</Option>
                        </Select>
                    </Form.Item>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                        <Button onClick={handleCancel} style={{ height: 40, borderRadius: 8, padding: '0 24px' }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ height: 40, borderRadius: 8, padding: '0 24px', fontWeight: 600 }}>
                            {editingId ? 'Save Changes' : 'Register Vehicle'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default VehiclesPage;




